import type { CityConfig } from "../city-template/types";

export const santaMonicaConfig: CityConfig = {
  city: "Santa Monica",
  state: "California",
  stateAbbr: "CA",
  slug: "santa-monica",
  route: "/santa-monica",
  title: "Emergency Passport Santa Monica, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Santa Monica, CA — as fast as 24 hours. Fast passport renewal, same-day help, and lost passport replacement for West LA travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Santa Monica, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or urgent passport help in Santa Monica? We help West Los Angeles travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Santa%20Monica%20California%20pier%20at%20golden%20hour%20with%20Pacific%20Ocean%20waves%2C%20Ferris%20wheel%20and%20amusement%20park%20visible%2C%20warm%20sunset%20light%20reflecting%20off%20the%20water%2C%20palm%20trees%20along%20the%20beachfront%20promenade%2C%20professional%20coastal%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20iconic%20West%20Los%20Angeles%20coastal%20atmosphere&width=1800&height=700&seq=santa-monica-hero-001&orientation=landscape",
  heroImageAlt: "Santa Monica California pier at golden hour with Pacific Ocean and Ferris wheel",
  geoPosition: "34.0195;-118.4912",
  geoPlacename: "Santa Monica, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 10–20 minutes from Santa Monica. Santa Monica residents: the LA Passport Agency at 11000 Wilshire is right next door.",
  },
  nearbyCitiesZips: [
    { name: "Santa Monica", zips: "90401–90405, 90409, 90411" },
    { name: "Venice", zips: "90291, 90294" },
    { name: "Brentwood", zips: "90049, 90073" },
    { name: "Pacific Palisades", zips: "90272" },
    { name: "Malibu", zips: "90263, 90264, 90265" },
    { name: "West Hollywood", zips: "90046, 90069" },
    { name: "Beverly Hills", zips: "90210, 90211, 90212" },
    { name: "Culver City", zips: "90230, 90232, 90233" },
    { name: "Marina del Rey", zips: "90292, 90295" },
    { name: "Playa del Rey", zips: "90293" },
    { name: "Westwood", zips: "90024, 90025" },
    { name: "Los Angeles", zips: "90001–90017" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent West Los Angeles departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Los Angeles", route: "/los-angeles" },
    { name: "Fast passport renewal in San Diego", route: "/san-diego" },
    { name: "Emergency passport help in San Francisco", route: "/san-francisco" },
    { name: "Urgent passport services near Pasadena", route: "/pasadena" },
    { name: "Same-day passport help in Long Beach", route: "/long-beach" },
    { name: "Rush passport help in Irvine", route: "/irvine" },
    { name: "Passport courier services in Anaheim", route: "/anaheim" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Santa Monica remotely via secure courier. We do not have a walk-in office in Santa Monica — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for West Los Angeles travelers.",
  fpcOfficeAddress: "225 S Lake Ave, Pasadena, CA 91101",
  fpcOfficeDistance: "Serving all of West Los Angeles",
  serviceAreaIntro: "From the Santa Monica Pier to Montana Avenue, from the Third Street Promenade to the Montana Avenue shops — Fast Passport Center serves the entire Santa Monica community. We regularly help travelers from Venice, Brentwood, Pacific Palisades, and Malibu get their passports fast.",
  nearbyCitiesIntro: "We serve all Santa Monica neighborhoods and nearby West Los Angeles communities including Venice, Brentwood, Pacific Palisades, Malibu, West Hollywood, and Beverly Hills. If you are on the Westside or near the coast, we have you covered.",
  howItWorksIntro: "Most Santa Monica travelers complete everything online and ship documents overnight. No need to drive downtown or wait in line at the regional agency.",
  mistakesIntro: "With LAX just 20 minutes away and Santa Monica being a major hub for international travelers, West LA residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Santa Monica and West Los Angeles travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about emergency passports, expedited passports, or fast passport renewal in Santa Monica? Here are the most common ones we hear from West Los Angeles travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Santa Monica travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast passport renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-alert-line",
      title: "Lost Passport Replacement",
      description: "Emergency passport replacement for lost or stolen passports with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Expedited passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-time-line",
      title: "Same-Day Passport Help",
      description: "Urgent same-day passport assistance for West LA travelers with flights within 24–72 hours.",
      bullets: ["Same-day document review", "Prepaid overnight shipping", "Direct courier hand-delivery"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Registered courier service that hand-delivers your passport documents directly to State Department processing facilities.",
      bullets: ["Registered State Department courier", "Bypass standard mail delays", "Trackable delivery both ways"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent emergency passport processing for Santa Monica travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Fast expedited passport processing for Santa Monica travelers with urgent international travel." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for lost or stolen passports in Santa Monica, including DS-64 form guidance." },
    { name: "Child Passport", description: "Expedited passport services for minors under 16 in Santa Monica, with parental consent guidance." },
    { name: "Passport Courier Service", description: "Registered passport courier service for Santa Monica that hand-delivers documents directly to State Department facilities." },
  ],
  testimonials: [
    {
      name: "Jessica P.",
      location: "Santa Monica, CA",
      rating: 5,
      text: "My fashion week trip to Paris was in 2 days and my passport was expired. Fast Passport Center got me a new one in 28 hours. Saved my career.",
      service: "Emergency Passport",
    },
    {
      name: "Daniel R.",
      location: "Venice, CA",
      rating: 5,
      text: "Needed to renew for a film shoot in Toronto. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Laura S.",
      location: "Brentwood, CA",
      rating: 5,
      text: "Lost my passport a week before my safari to Kenya. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Chris A.",
      location: "Pacific Palisades, CA",
      rating: 5,
      text: "As a frequent international business traveler from the Westside, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "santa-monica-1",
      name: "Santa Monica Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1650 5th St",
      city: "Santa Monica",
      state: "CA",
      zip: "90401",
      phone: "(310) 393-5967",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Santa Monica Pier",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-monica-2",
      name: "Venice Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2211 Lincoln Blvd",
      city: "Venice",
      state: "CA",
      zip: "90291",
      phone: "(310) 392-6961",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Venice Beach",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-monica-3",
      name: "Culver City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11163 Jefferson Blvd",
      city: "Culver City",
      state: "CA",
      zip: "90230",
      phone: "(310) 391-2489",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from Culver City Plaza",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Westside residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-monica-4",
      name: "Beverly Hills Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "312 N Robertson Blvd",
      city: "Beverly Hills",
      state: "CA",
      zip: "90211",
      phone: "(310) 274-8131",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Beverly Hills City Hall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for affluent Westside residents. Book appointments ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-monica-5",
      name: "Marina del Rey Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4345 Glencoe Ave",
      city: "Marina del Rey",
      state: "CA",
      zip: "90292",
      phone: "(310) 823-1411",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Marina del Rey Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal Westside residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "santa-monica-6",
      name: "LA County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "12400 Imperial Hwy",
      city: "Norwalk",
      state: "CA",
      zip: "90650",
      phone: "(562) 462-2133",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "25 miles from Santa Monica",
      website: "https://www.lavote.net/",
      websiteLabel: "LA County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Westside Coverage", agency: "No West LA coastal expertise", fpc: "Santa Monica Pier, Silicon Beach tech corridor, and Westside coastal travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "West LA photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at West LA acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Santa Monica applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many West LA families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Santa Monica travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Santa Monica." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Santa Monica address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Santa Monica before international travel?",
      a: "Yes. Fast Passport Center provides emergency passport services in Santa Monica for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most West LA travelers.",
    },
    {
      q: "How fast can I renew my passport in Santa Monica?",
      a: "Fast Passport Center offers fast passport renewal in Santa Monica in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent.",
    },
    {
      q: "What if my passport expires before my cruise?",
      a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Santa Monica in 24–48 hours so you don't miss your departure.",
    },
    {
      q: "Can I get a same-day passport appointment near Santa Monica?",
      a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to West LA residents.",
    },
    {
      q: "Can I expedite a child passport in Santa Monica?",
      a: "Yes. Fast Passport Center expedites child passports in Santa Monica for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk West LA families through the entire process.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Santa Monica with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours.",
    },
    {
      q: "How fast can I get a passport in Santa Monica?",
      a: "With Fast Passport Center's emergency courier service, Santa Monica travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Santa Monica?",
      a: "Popular options include the Santa Monica Main Post Office (90401), Venice Post Office (90291), and Culver City Post Office (90230). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Santa Monica?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Malibu or Pacific Palisades?",
      a: "We serve all of West Los Angeles including Malibu, Pacific Palisades, Brentwood, Venice, and Marina del Rey. Our overnight shipping labels work from any UPS location, so your location on the coast doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Santa Monica?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Santa Monica?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Santa Monica and Venice offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Santa Monica?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Santa Monica?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk West Los Angeles families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Santa Monica", address: "710 Broadway, Santa Monica, CA 90401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Venice", address: "201 Lincoln Blvd, Venice, CA 90291", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Santa Monica", address: "1401 Wilshire Blvd, Santa Monica, CA 90403", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Brentwood", address: "11950 San Vicente Blvd, Los Angeles, CA 90049", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Culver City", address: "5857 W Century Blvd, Los Angeles, CA 90045", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Santa Monica", address: "510 Wilshire Blvd, Santa Monica, CA 90401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Venice", address: "1600 Main St, Venice, CA 90291", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Marina del Rey", address: "13463 Washington Blvd, Marina del Rey, CA 90292", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Santa Monica",
      address: "2235 Colorado Ave, Santa Monica, CA 90404",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Culver City",
      address: "11400 W Washington Blvd, Culver City, CA 90230",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Hollywood",
      address: "8033 Sunset Blvd, Los Angeles, CA 90046",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — West Hollywood",
      address: "936 N Formosa Ave, West Hollywood, CA 90046",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Convenient for Westside residents. Full-service center.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "12 miles from Santa Monica",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "The primary international airport for Los Angeles. Major hub for international flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: true,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "20 miles from Santa Monica",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Smaller airport with domestic flights and some international connections. Easier access than LAX for some Westside residents.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "50 miles from Santa Monica",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Orange County airport with international flights. Alternative to LAX for some Westside travelers.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for Los Angeles County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the LA County Registrar-Recorder.", url: "https://www.lavote.net/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through LA County Superior Court.", url: "https://www.lacourt.org/" }],
};